import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { Box, Flex, Heading, Select, Stack, Text } from "@chakra-ui/react";
import baseService from "../../services/base";
import Loader from "../Feedback/Loader";
import CertificationDashboard from "./CertificationDashboard";
import MaturityDashboard from "./MaturityDashboard";
import { TODOS, exerciciosDe, exibiveis, filtrar } from "./filtrosDeCertificado";

// Extraído de pages/results/index.jsx (era o painel "Por empresa" da antiga
// tela de abas) pra virar página própria em /dashboards/por-empresa. Mesmo
// componente, mesmo comportamento — só mudou de arquivo. O nome exportado
// continua `CompanyResults` de propósito: index.test.jsx importa por esse
// nome e cobre os filtros de exercício/certificação.

const fetcher = (url) => baseService.get(url).then((r) => r?.data);

export function CompanyResults() {
  const [companyId, setCompanyId] = useState("");
  // EG-55: a Visão geral renderiza um bloco por certificado com pilar, e
  // fragmentar por tema fez cada bloco crescer. Estes dois recortes evitam que
  // empresa com muitas certificações vire rolagem infinita.
  const [exercicio, setExercicio] = useState(TODOS);
  const [certificadoId, setCertificadoId] = useState(TODOS);

  // MASTER vê as empresas top-level via findAll (mesmo endpoint da tela Empresas).
  // admin_company/:id filtra por links_admin_company (papel RESPONSAVEL) → vazio p/ MASTER.
  const { data: companies, isLoading } = useSWR("company", fetcher);

  useEffect(() => {
    if (!companyId && Array.isArray(companies) && companies.length > 0) {
      setCompanyId(String(companies[0].id_company));
    }
  }, [companies, companyId]);

  const { data: certificates } = useSWR(
    companyId ? `certificate/score/${companyId}` : null,
    fetcher,
    { onErrorRetry: (error) => { if (error?.status === 500) return; } }
  );

  const { data: diagnoses } = useSWR(
    companyId ? `survey/diagnostic/all/result?id_company=${companyId}` : null,
    fetcher
  );

  const certs = useMemo(() => (Array.isArray(certificates) ? certificates : []), [certificates]);
  const diags = useMemo(() => (Array.isArray(diagnoses) ? diagnoses : []), [diagnoses]);

  const exercicios = useMemo(() => exerciciosDe(certs), [certs]);

  // O select de certificação só oferece o que o dashboard sabe desenhar (tem
  // pilar) e o que está no exercício escolhido — oferecer um certificado de "só
  // nota" levaria o usuário a escolher algo e não ver nada.
  const certificadosDoRecorte = useMemo(
    () => exibiveis(filtrar(certs, { exercicio })),
    [certs, exercicio]
  );

  const certsVisiveis = useMemo(
    () => filtrar(certs, { exercicio, certificado: certificadoId }),
    [certs, exercicio, certificadoId]
  );

  // Trocar o exercício invalida a certificação escolhida: ela pode não existir
  // no novo recorte, e a tela ficaria vazia com dois filtros preenchidos e
  // nenhuma pista do motivo.
  const trocarExercicio = (valor) => {
    setExercicio(valor);
    setCertificadoId(TODOS);
  };

  if (isLoading) return <Loader />;

  const hasCompanies = Array.isArray(companies) && companies.length > 0;

  if (!hasCompanies) {
    return (
      <Flex py="14px">
        <Text fontSize="15px" fontWeight={500}>
          Nenhuma empresa disponível.
        </Text>
      </Flex>
    );
  }

  return (
    <Box>
      <Flex justify="flex-end" mb="24px" gap="12px" flexWrap="wrap">
        <Select
          aria-label="Empresa"
          w={{ base: "100%", md: "320px" }}
          h="48px"
          bg="white"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
        >
          {companies.map((company) => (
            <option key={company.id_company} value={company.id_company}>
              {company.name}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Exercício"
          w={{ base: "100%", md: "160px" }}
          h="48px"
          bg="white"
          value={exercicio}
          onChange={(e) => trocarExercicio(e.target.value)}
        >
          {/* "Todos" não é atalho para o ano mais recente: há certificado sem
              exercício cadastrado, e é só aqui que ele aparece. */}
          <option value={TODOS}>Todos</option>
          {exercicios.map((ano) => (
            <option key={ano} value={ano}>
              {ano}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Certificação"
          w={{ base: "100%", md: "280px" }}
          h="48px"
          bg="white"
          value={certificadoId}
          onChange={(e) => setCertificadoId(e.target.value)}
        >
          <option value={TODOS}>Todos</option>
          {certificadosDoRecorte.map((c) => (
            <option key={c.id_certificate} value={c.id_certificate}>
              {c.name}
            </option>
          ))}
        </Select>
      </Flex>

      <Stack spacing="32px">
        <Box>
          <Heading fontSize="18px" fontWeight={700} mb="16px">
            Visão geral
          </Heading>
          <CertificationDashboard certificates={certsVisiveis} />
        </Box>

        <Box>
          <Heading fontSize="18px" fontWeight={700} mb="16px">
            Maturidade ESG por pilar
          </Heading>
          <MaturityDashboard diagnoses={diags} />
        </Box>
      </Stack>
    </Box>
  );
}

export default CompanyResults;
